import { create } from "zustand";

interface TabBarVisibleState {
  /** Number of currently-mounted tab bars. In practice 0 or 1, but a
   * counter (rather than a boolean flipped in each cleanup) survives a
   * screen's `TabBar` unmounting after the next screen's has already
   * mounted — a route transition can run effects out of order — without
   * ever reporting "no tab bar" while one is in fact still on screen. */
  count: number;
  visible: boolean;
}

/**
 * Whether `components/today/v2/TabBar` — the app's one live tab bar — is
 * currently mounted anywhere on screen. `<Toast/>` (Task 7b, DUE-318) reads
 * this to sit 12 above the floating bar's top edge or `bottom: 24` on a screen
 * that renders none, per 05-SHARED.md's Toast row. `TabBar` is the only
 * writer, via `registerTabBarMounted` below — this is "the same way the
 * tab bar itself decides to render" rather than a route-matching guess,
 * since visibility is a per-screen `bar` prop (`YouFrame`, `FriendsFrame`)
 * that no route table captures.
 */
export const useTabBarVisibleStore = create<TabBarVisibleState>(() => ({
  count: 0,
  visible: false,
}));

/** Call from `TabBar`'s mount effect; call the returned cleanup on unmount. */
export function registerTabBarMounted(): () => void {
  useTabBarVisibleStore.setState((s) => ({ count: s.count + 1, visible: true }));
  return () => {
    useTabBarVisibleStore.setState((s) => {
      const count = Math.max(0, s.count - 1);
      return { count, visible: count > 0 };
    });
  };
}
